import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { MdDownload, MdErrorOutline, MdLock, MdVisibility } from 'react-icons/md'
import { getClient } from '@/lib/api/client'
import { formatBytes, colorOf, iconOf } from '@/pages/admin/core/storage/storage-utils'

interface ShareInfo {
    requiresPassword: boolean
    allowDownload: boolean
    watermark: boolean
    expiresAt: string | null
    file: {
        name: string
        mimeType: string
        sizeBytes: string
        extension: string
    } | null
}

/**
 * Página de um arquivo compartilhado por link.
 *
 * Vive fora da área autenticada: quem abre é o paciente ou o parceiro que
 * recebeu o link, sem conta no sistema. As chamadas usam o cliente HTTP sem
 * token, e toda a validação (expiração, senha, teto de downloads) acontece no
 * servidor.
 */
export default function SharedFilePage() {
    const { token } = useParams<{ token: string }>()

    const [info, setInfo] = useState<ShareInfo | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [password, setPassword] = useState('')
    const [resolving, setResolving] = useState(false)

    const load = useCallback(async () => {
        if (!token) return
        setLoading(true)

        try {
            const client = await getClient()
            const response = await client.get(`/storage/public/${token}`)
            setInfo(response.data?.result ?? response.data)
        } catch (requestError: any) {
            setError(
                requestError?.response?.data?.message ||
                    'Link inválido, expirado ou revogado',
            )
        } finally {
            setLoading(false)
        }
    }, [token])

    useEffect(() => {
        void load()
    }, [load])

    const handleAccess = async (inline: boolean) => {
        if (!token) return
        setResolving(true)
        setError('')

        try {
            const client = await getClient()
            const response = await client.post(`/storage/public/${token}/download`, {
                password: password || undefined,
            })
            const url = (response.data?.result ?? response.data)?.url
            if (!url) throw new Error('Resposta inválida do servidor')

            window.open(url, inline ? '_blank' : '_self', 'noopener')
        } catch (requestError: any) {
            setError(
                requestError?.response?.data?.message ||
                    'Não foi possível abrir o arquivo',
            )
        } finally {
            setResolving(false)
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-base-200">
                <span className="loading loading-spinner loading-lg text-primary" />
            </div>
        )
    }

    if (!info || !info.file) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
                <div className="card bg-base-100 shadow-lg max-w-md w-full">
                    <div className="card-body items-center text-center">
                        <MdErrorOutline className="text-6xl text-error" />
                        <h1 className="text-xl font-semibold mt-2">Link indisponível</h1>
                        <p className="text-base-content/70">
                            {error || 'Este link é inválido, expirou ou foi revogado.'}
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    const Icon = iconOf(info.file.mimeType, info.file.extension)

    return (
        <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
            <div className="card bg-base-100 shadow-lg max-w-md w-full">
                <div className="card-body">
                    <div className="flex flex-col items-center text-center">
                        <Icon
                            className={`text-7xl ${colorOf(
                                info.file.mimeType,
                                info.file.extension,
                            )}`}
                        />
                        <h1 className="text-lg font-semibold mt-3 break-all">
                            {info.file.name}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {formatBytes(info.file.sizeBytes)}
                        </p>
                        {info.expiresAt && (
                            <p className="text-xs text-base-content/50 mt-1">
                                Disponível até{' '}
                                {new Date(info.expiresAt).toLocaleDateString('pt-BR')}
                            </p>
                        )}
                    </div>

                    {info.requiresPassword && (
                        <div className="form-control w-full mt-6">
                            <label className="label pb-1.5" htmlFor="shared-file-password">
                                <span className="label-text font-medium flex items-center gap-1.5">
                                    <MdLock />
                                    Senha de acesso
                                </span>
                            </label>
                            <input
                                id="shared-file-password"
                                type="password"
                                className="input input-bordered w-full"
                                value={password}
                                placeholder="Informe a senha recebida"
                                onChange={event => setPassword(event.target.value)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter') void handleAccess(true)
                                }}
                            />
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-error mt-4 py-2">
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2 mt-6">
                        <button
                            type="button"
                            className="btn btn-outline flex-1"
                            onClick={() => void handleAccess(true)}
                            disabled={resolving}
                        >
                            <MdVisibility />
                            Visualizar
                        </button>
                        {info.allowDownload && (
                            <button
                                type="button"
                                className="btn btn-primary flex-1"
                                onClick={() => void handleAccess(false)}
                                disabled={resolving}
                            >
                                {resolving ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    <MdDownload />
                                )}
                                Baixar
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
